// src/api/services/blogService.js
import api from '../interceptors/axiosInterceptor'
import API_CONFIG from '../config'

const BLOG_ENDPOINTS = {
  GET_ALL: '/blog/all/',
  GET_DETAIL: '/blog/get/',
  CREATE_COMMENT: '/blog/new-comment/',
  GET_COMMENTS: '/blog/comments/',
  GET_ALL_COMMENTS: '/blog/comments/', // مسیر عمومی برای دریافت همه کامنت‌ها (فرض بر این است که بدون slug کار می‌کند یا نیاز به اسلاگ دارد)
}

// دریافت slug فروشگاه
const getStoreSlug = () => {
  return window.__STORE_INFO__.slug;
};

const storeSlug = getStoreSlug();

const BlogService = {
  /**
   * دریافت لیست تمام مقالات
   */
  async getAllBlog() {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${BLOG_ENDPOINTS.GET_ALL}${storeSlug}/`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * دریافت جزئیات یک مقاله
   */
  async getBlog(slug) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${BLOG_ENDPOINTS.GET_DETAIL}${slug}/${storeSlug}/`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * دریافت همه کامنت‌های سایت (برای سایدبار)
   * نکته: اگر سرور شما برای این اندپوینت نیاز به store_slug دارد، مسیر زیر را تغییر دهید.
   * فعلاً فرض می‌کنیم مسیر عمومی است یا همان مسیر کامنت‌ها با پارامتر store_slug کار می‌کند.
   */
  async getAllComments() {
    try {
      let baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');

      // تلاش اول: مسیر عمومی (اگر سرور پشتیبانی کند)
      // اگر خطا داد، احتمالاً باید store_slug داشته باشد.
      // ما اینجا فرض می‌کنیم مسیر دقیقاً مشابه GET_COMMENTS است اما بدون slug مقاله
      const url = `${baseUrl}${BLOG_ENDPOINTS.GET_COMMENTS}${storeSlug}/`;

      const response = await api.get(url);

      // بررسی اینکه آیا پاسخ آرایه است یا آبجوت دارای data
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.comments)) {
        return response.data.comments;
      } 

      return [];

    } catch (error) {
      return [];
    }
  },

  /**
   * ایجاد کامنت جدید
   */
  async createComment(commentData) {
    try {
      const response = await api.post(
        `${API_CONFIG.BASE_URL}${BLOG_ENDPOINTS.CREATE_COMMENT}`,
        commentData
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  /**
   * دریافت کامنت‌های یک مقاله خاص
   */
  async getBlogComments(blogSlug) {
    try {
      const response = await api.get(
        `${API_CONFIG.BASE_URL}${BLOG_ENDPOINTS.GET_COMMENTS}${storeSlug}/${blogSlug}/`
      )
      return response.data
    } catch (error) {
      throw this.handleError(error)
    }
  },

  // ... سایر توابع کمکی (normalizeBlogData, handleError, etc.) ...

  normalizeBlogData(data) {
    if (!data) return null;
    const fullText = data.content || data.body || data.text || data.description || '';
    const excerpt = data.excerpt || data.short_description || (fullText.substring(0, 150) + '...');
    return {
      ...data,
      text: fullText,
      excerpt: excerpt,
      image: data.image || data.cover_image || null
    };
  },

  handleError(error) {
    let message = 'خطایی ناشناخته رخ داد.';
    let status = -1;
    let data = null;

    if (error.response) {
      const responseData = error.response.data;
      status = error.response.status;
      data = responseData;

      if (typeof responseData === 'object') {
        message = responseData.message || responseData.detail || responseData.error || 'خطای سرور';
      } else if (typeof responseData === 'string') {
        message = responseData;
      }
    } else if (error.request) {
      message = 'اتصال به سرور برقرار نشد. لطفاً اینترنت خود را بررسی کنید.';
      status = 0;
    } else {
      message = error.message || 'خطا در پردازش درخواست';
    }

    return { message, status, data };
  },

  prepareCommentData(blogSlug, data) {
    return {
      store_slug: storeSlug,
      blog_slug: blogSlug,
      rate: data.rate,
      text: data.text,
      reply_to: data.reply_to || null
    }
  },

  formatDate(dateString) {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  },

  calculateAverageRating(comments) {
    if (!comments || comments.length === 0) return 0;
    const total = comments.reduce((sum, comment) => sum + (comment.rate || 0), 0);
    return Math.round((total / comments.length) * 10) / 10;
  },

  getCommentStats(comments) {
    const stats = { total: 0, average: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
    if (!comments || comments.length === 0) return stats;

    const flattenComments = (commentsList) => {
      let allComments = [];
      commentsList.forEach(comment => {
        allComments.push(comment);
        if (comment.replies && Array.isArray(comment.replies)) {
          allComments = [...allComments, ...flattenComments(comment.replies)];
        }
      });
      return allComments;
    };

    const allComments = flattenComments(comments);
    stats.total = allComments.length;

    if (stats.total > 0) {
      const totalRating = allComments.reduce((sum, comment) => {
        const rate = parseInt(comment.rate) || 0;
        if (rate >= 1 && rate <= 5) {
          stats.distribution[rate]++;
        }
        return sum + rate;
      }, 0);
      stats.average = Math.round((totalRating / stats.total) * 10) / 10;
    }
    return stats;
  }
}

export default BlogService;